// Escena de partida en Three.js: fichas 3D, atril, arrastre/toque, snap y efectos.
// Se sincroniza SIEMPRE desde el estado lógico (Game). No guarda estado de juego propio.
import * as THREE from 'three';
import { getTileTexture, disposeTileTextures } from './tileTexture.js';
import { EVENTS } from '../utils/constants.js';

const UNIT = 0.95;             // ancho base de una ficha (reducido: fichas más pequeñas)
const DEPTH = 1.25;            // profundidad (z) de una ficha
const THICK = 0.3;            // grosor
const GAP = 0.12;              // separación entre fichas
const BOARD_ANCHOR_Z = -0.7;   // z de la PRIMERA fila (algo por encima del centro)
const ROW_Z_STEP = 2.4;        // separación entre filas; las nuevas crecen HACIA ARRIBA (lejos del atril)
const RACK_Z = 3.2;            // z del atril (nunca lo invaden las palabras del tablero)
const LIFT = 0.9;              // elevación al arrastrar

function tileWidth(tile) {
  return UNIT + (Math.max(1, tile.widthUnits) - 1) * 0.72;
}

export class GameScene {
  constructor(sceneManager, game, bus) {
    this.sm = sceneManager;
    this.game = game;
    this.bus = bus;
    // Ejecuta una acción del juego. App la sobrescribe para enrutar por red (multijugador).
    this.commit = (method, ...args) => this.game[method](...args);

    this.group = new THREE.Group();
    this.sm.scene.add(this.group);
    this.sm.resetView(); // cada partida empieza con zoom/desplazamiento por defecto

    this.meshes = new Map();     // instanceId -> mesh
    this.rowLabels = new Map();  // wordId -> contorno
    this._wordValidity = new Map(); // wordId -> bool (para detectar cuándo pasa a válida)
    this._pops = [];             // animaciones "pop" al validar una palabra
    this._rings = [];            // ondas expansivas al conseguir una palabra
    this._fitMode = true;        // true: la vista encaja SIEMPRE todas las palabras
    this._lastTap = null;        // para detectar doble toque
    this._userArranged = false;  // el jugador pidió reorganizar (doble toque): mantener el orden
                                 // tras cada sync en red (si no, applyRemoteState lo deshace)
    this.targets = { rack: null, rows: [] }; // zonas de drop calculadas en cada layout

    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0); // plano lógico y=0

    this.dragged = null;         // { mesh, tile, from }
    this.selected = null;        // ficha seleccionada por toque
    this._pointerDownPos = null;
    this._particles = [];

    this._geomTop = new Map();   // cache de geometrías por ancho
    this._bind();

    // Actualizador para animaciones (lerp de posiciones + partículas).
    this._removeUpdater = this.sm.addUpdater((dt) => this._update(dt));

    // Sincronización con la lógica.
    this._offBoard = this.bus.on(EVENTS.BOARD_CHANGED, () => this.sync());
    // Al cambiar de turno (tras jugar una IA o confirmar): si el jugador pidió ordenar
    // (doble toque), se MANTIENE el orden reorganizando de nuevo. No lo hacemos en cada
    // BOARD_CHANGED para no mover las fichas mientras el humano construye su palabra.
    this._offTurn = this.bus.on(EVENTS.TURN_START, () => {
      if (this._userArranged && this.game.board) this._reorganizeWords(this.game.board);
      this.sync();
    });
    this._offEvo = this.bus.on(EVENTS.WORD_EVOLVED, (ev) => this.playEvolutionEffect(ev));
    this._offValidated = this.bus.on(EVENTS.WORD_VALIDATED, (v) => this._applyValidation(v));
  }

  /** Solo el jugador humano puede interactuar, y solo si la partida no ha terminado. */
  get _interactive() {
    const s = this.game.state;
    return !!s && !s.finished && this.game.isSelfTurn();
  }

  // ---------------------------------------------------------------------------
  // Sincronización estado -> escena
  // ---------------------------------------------------------------------------
  /** Jugador humano local: su atril es el ÚNICO que se muestra (nunca el de la IA). */
  _humanPlayer() {
    return this.game.selfPlayer || this.game.players.find((p) => !p.isAI) || this.game.players[0];
  }

  sync() {
    if (!this.game.state) return;
    const board = this.game.board;

    // El 3D SOLO dibuja el TABLERO. Las fichas de la mano viven en la mano de cartas HTML
    // (HandPanel), separadas de la mesa.
    const present = new Set(board.allTiles().map((t) => t.instanceId));

    // Elimina meshes de fichas que ya no están en el tablero (mano, confirmadas por IA, etc.).
    for (const [id, mesh] of this.meshes) {
      if (!present.has(id)) {
        this._disposeMesh(mesh);
        this.meshes.delete(id);
      }
    }

    // Asegura meshes y calcula posiciones objetivo.
    this._layoutBoard(board);
    // Las palabras NO pueden solaparse: si dos se pisan, se separan a filas distintas.
    if (this._separateWords(board)) this._layoutBoard(board);
    this._updateRowLabels(board);

    // Auto-encaje: al aparecer una palabra/ficha NUEVA en la mesa, se reajusta el zoom
    // para que TODAS las palabras se vean (aunque el usuario hubiera hecho zoom manual).
    const tileCount = board.allTiles().length;
    const wordCount = board.words.length;
    if (tileCount > (this._prevTileCount || 0) || wordCount > (this._prevWordCount || 0)) {
      this._fitMode = true;
    }
    this._prevTileCount = tileCount;
    this._prevWordCount = wordCount;

    this._updateCameraFraming();
  }

  /**
   * Evita que dos palabras se solapen en la mesa. Recorre las palabras y, si una pisa a
   * otra anterior (se cruzan en X y comparten fila Z), la empuja a una fila superior libre.
   * Devuelve true si movió alguna (para recalcular el layout).
   */
  _separateWords(board) {
    const words = board.words.filter((w) => w.x !== undefined && w.z !== undefined);
    if (words.length < 2) return false;
    const halfW = (w) => w.tiles.reduce((s, t) => s + tileWidth(t) + GAP, -GAP) / 2;
    const clash = (a, b) => {
      const xOver = Math.abs(a.x - b.x) < halfW(a) + halfW(b) + GAP;
      const zOver = Math.abs(a.z - b.z) < ROW_Z_STEP * 0.9;
      return xOver && zOver;
    };
    let moved = false;
    for (let i = 1; i < words.length; i++) {
      let guard = 0;
      while (guard++ < 60) {
        const c = words.slice(0, i).find((w) => clash(words[i], w));
        if (!c) break;
        // Empuja esta palabra a la fila inmediatamente superior a la que pisa (z decreciente).
        words[i].z = c.z - ROW_Z_STEP;
        moved = true;
      }
    }
    return moved;
  }

  /** Límites (mundo) del contenido de la mesa, o un valor por defecto si está vacía. */
  _contentBounds() {
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const mesh of this.meshes.values()) {
      const t = mesh.userData.target;
      const hw = tileWidth(mesh.userData.tile) / 2;
      minX = Math.min(minX, t.x - hw); maxX = Math.max(maxX, t.x + hw);
      minZ = Math.min(minZ, t.z - DEPTH / 2); maxZ = Math.max(maxZ, t.z + DEPTH / 2);
    }
    if (!isFinite(minX)) return { minX: -4.5, maxX: 4.5, minZ: -2, maxZ: 2 };
    return { minX, maxX, minZ, maxZ };
  }

  /** En modo "encajar", ajusta la cámara para que TODAS las palabras se vean en pantalla. */
  _updateCameraFraming() {
    const b = this._contentBounds();
    this.sm.contentBounds = b; // el SceneManager lo usa para no dejar sacar el tablero de pantalla
    if (!this._fitMode) return;
    this.sm.fitTo(b);
  }

  /** Convierte un punto de pantalla (clientX,clientY) a un punto del plano de la mesa. */
  screenToBoard(clientX, clientY) {
    const w = this.sm.vw();
    const h = this.sm.vh();
    this.pointer.x = (clientX / w) * 2 - 1;
    this.pointer.y = -(clientY / h) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.sm.camera);
    const pt = new THREE.Vector3();
    this.raycaster.ray.intersectPlane(this.plane, pt);
    return pt;
  }

  /**
   * Coloca una ficha ARRASTRADA DESDE LA MANO en el tablero, en el punto de pantalla dado.
   * Si cae cerca de una palabra, se une; si no, crea una palabra nueva en esa posición.
   */
  placeFromHand(instanceId, clientX, clientY) {
    const pt = this.screenToBoard(clientX, clientY);
    if (!pt) return;
    const near = this._nearestWord(pt, instanceId);
    if (near) {
      const index = this._indexInRow(near, pt.x, instanceId);
      this.commit('placeTileFromRack', instanceId, near.wordId, index);
    } else {
      this.commit('placeTileFromRack', instanceId, null, -1, { x: pt.x, z: Math.min(pt.z, RACK_Z - 0.5) });
    }
    this.bus.emit(EVENTS.PLAY_SFX, { name: 'snap' });
  }

  /** Regenera todas las texturas de ficha (p. ej. cuando termina de cargar la fuente). */
  refreshTileTextures() {
    disposeTileTextures();
    for (const mesh of this.meshes.values()) {
      const tile = mesh.userData.tile;
      if (!tile) continue;
      mesh.material.map = getTileTexture(tile);
      mesh.material.needsUpdate = true;
    }
  }

  _ensureMesh(tile) {
    let mesh = this.meshes.get(tile.instanceId);
    if (mesh) {
      // Actualiza textura si cambió el texto asignado (comodín).
      if (mesh.userData.text !== (tile.assignedText || tile.text)) {
        mesh.material.map = getTileTexture(tile);
        mesh.material.needsUpdate = true;
        mesh.userData.text = tile.assignedText || tile.text;
      }
      return mesh;
    }
    // Ficha = PLANO plano (carta), sin iluminación ni sombra: idéntica a la mano.
    const w = tileWidth(tile);
    let geo = this._geomTop.get(w);
    if (!geo) {
      geo = new THREE.PlaneGeometry(w, DEPTH);
      geo.rotateX(-Math.PI / 2); // tumbado sobre la mesa, cara hacia arriba
      this._geomTop.set(w, geo);
    }
    const mat = new THREE.MeshBasicMaterial({
      map: getTileTexture(tile), transparent: true, side: THREE.DoubleSide, depthWrite: false,
    });
    mesh = new THREE.Mesh(geo, mat);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.renderOrder = 5; // por encima del outline
    mesh.userData = { instanceId: tile.instanceId, tile, text: tile.assignedText || tile.text, target: new THREE.Vector3(), isNew: true };
    this.group.add(mesh);
    this.meshes.set(tile.instanceId, mesh);
    return mesh;
  }

  _layoutBoard(board) {
    this.targets.rows = [];
    for (const word of board.words) {
      // Posición LIBRE de la palabra (estilo Rummikub). Si no tiene, se le asigna un hueco.
      if (word.x === undefined || word.z === undefined) {
        const p = this._autoPlace(word);
        word.x = p.x; word.z = p.z;
      }
      const z = word.z;
      const totalW = word.tiles.reduce((s, t) => s + tileWidth(t) + GAP, -GAP);
      let x = word.x - totalW / 2; // centrada en su ancla (word.x)
      const centers = [];
      for (const tile of word.tiles) {
        const w = tileWidth(tile);
        const mesh = this._ensureMesh(tile);
        const cx = x + w / 2;
        mesh.userData.target.set(cx, THICK / 2, z);
        // Ficha recién creada: colócala directamente en su sitio (sin volar desde el centro).
        if (mesh.userData.isNew) { mesh.position.copy(mesh.userData.target); mesh.userData.isNew = false; }
        mesh.userData.wordId = word.id;
        centers.push({ x: cx, w, id: tile.instanceId });
        x += w + GAP;
      }
      this.targets.rows.push({ wordId: word.id, x: word.x, z, centers, halfW: totalW / 2 });
    }
  }

  /** Busca un hueco libre para una palabra sin posición (p.ej. jugadas de la IA). */
  _autoPlace(word) {
    const placed = this.game.board.words.filter((w) => w !== word && w.x !== undefined);
    let z = BOARD_ANCHOR_Z;
    // Baja hacia arriba (z decreciente) hasta encontrar una banda z libre.
    let guard = 0;
    while (placed.some((w) => Math.abs(w.z - z) < ROW_Z_STEP * 0.9) && guard++ < 40) {
      z -= ROW_Z_STEP;
    }
    return { x: 0, z };
  }

  // ---------------------------------------------------------------------------
  // Contorno (outline) de palabra de 1 PÍXEL: VERDE si es válida, ROJO si no.
  // ---------------------------------------------------------------------------
  _updateRowLabels(board) {
    const alive = new Set();
    for (const word of board.words) {
      alive.add(word.id);
      const res = this.game.validator.validateWord(word.tiles);
      const totalW = word.tiles.reduce((s, t) => s + tileWidth(t) + GAP, -GAP);
      // Abraza EXACTAMENTE la forma de las fichas: mismo ancho/alto y esquinas redondeadas.
      const hw = totalW / 2 + 0.04;
      const hd = DEPTH / 2 + 0.04;
      const r = 0.16; // radio de esquina, igual que las fichas

      let outline = this.rowLabels.get(word.id);
      if (!outline) {
        outline = this._makeOutline();
        this.rowLabels.set(word.id, outline);
        this.group.add(outline);
      }
      this._setRoundedRect(outline.geometry, hw, hd, r, 0.02);
      outline.material.color.set(res.valid ? '#59e06a' : '#ff5a4d');
      outline.position.set(word.x ?? 0, 0, word.z ?? BOARD_ANCHOR_Z);
      // Medias-dimensiones del contorno (para colocar el badge "?" en su esquina sup-derecha).
      outline.userData.hw = hw;
      outline.userData.hd = hd;

      // Feedback al CONSEGUIR una palabra válida: sonido + "pop" + confeti + onda + destello VERDE.
      // Salta CADA VEZ que se logra una palabra válida NUEVA o DISTINTA: inválida->válida, y
      // también válida->OTRA válida (p.ej. añadir una letra a una palabra correcta y seguir siendo
      // correcta). Guardamos el TEXTO de la última palabra válida y comparamos.
      const prevValid = this._wordValidity.get(word.id); // texto válido anterior, o null/undefined
      const curValid = res.valid ? res.word : null;
      if (res.valid && curValid !== prevValid) {
        const wx = word.x ?? 0, wz = word.z ?? BOARD_ANCHOR_Z;
        this._pulseWord(word);
        this._burstAt(wx, wz, 46);
        this._ringAt(wx, wz);
        outline.userData.flash = 1; // destello del contorno verde
        this.bus.emit(EVENTS.PLAY_SFX, { name: 'formword' });
        this.bus.emit(EVENTS.VIBRATE, { ms: 24 });
      }
      this._wordValidity.set(word.id, curValid);
    }
    for (const [id, outline] of this.rowLabels) {
      if (!alive.has(id)) {
        this.group.remove(outline);
        outline.geometry.dispose();
        outline.material.dispose();
        this.rowLabels.delete(id);
        this._wordValidity.delete(id);
      }
    }
  }

  /** Anima un "pop" de escala en las fichas de una palabra recién validada. */
  _pulseWord(word) {
    const meshes = word.tiles.map((t) => this.meshes.get(t.instanceId)).filter(Boolean);
    if (meshes.length) this._pops.push({ meshes, t: 0 });
  }

  /** Ráfaga de confeti multicolor al formar una palabra válida (feedback atractivo). */
  _burstAt(x, z, count = 46) {
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const vel = [];
    const palette = ['#6ee87a', '#ffd54a', '#ff8a3d', '#ffffff', '#59e0c0', '#a880ff'].map((c) => new THREE.Color(c));
    for (let i = 0; i < count; i++) {
      pos[i * 3] = x + (Math.random() - 0.5) * 0.7;
      pos[i * 3 + 1] = 0.5;
      pos[i * 3 + 2] = z + (Math.random() - 0.5) * 0.7;
      const ang = Math.random() * Math.PI * 2;
      const spd = 1.7 + Math.random() * 2.7;
      vel.push(new THREE.Vector3(Math.cos(ang) * spd, Math.random() * 3.4 + 2.8, Math.sin(ang) * spd));
      const c = palette[(Math.random() * palette.length) | 0];
      colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b;
    }
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const mat = new THREE.PointsMaterial({ size: 0.26, transparent: true, vertexColors: true, depthWrite: false });
    const points = new THREE.Points(geo, mat);
    this.group.add(points);
    this._particles.push({ points, vel, life: 1.2, geo, mat });
  }

  /** Onda expansiva luminosa sobre la mesa al conseguir una palabra. */
  _ringAt(x, z, color = '#8affa6') {
    const geo = new THREE.RingGeometry(0.34, 0.46, 44);
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9, side: THREE.DoubleSide, depthWrite: false });
    const ring = new THREE.Mesh(geo, mat);
    ring.rotation.x = -Math.PI / 2;     // tumbada sobre la mesa
    ring.position.set(x, 0.06, z);
    ring.renderOrder = 4;
    this.group.add(ring);
    this._rings.push({ ring, t: 0, geo, mat });
  }

  /** Línea cerrada (LineLoop) de 1px con forma de rectángulo redondeado. */
  _makeOutline() {
    const geo = new THREE.BufferGeometry();
    const SEGS = 6; // segmentos por esquina
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(4 * (SEGS + 1) * 3), 3));
    geo.userData = { segs: SEGS };
    const mat = new THREE.LineBasicMaterial({ color: 0x59e06a, transparent: true, depthWrite: false });
    const loop = new THREE.LineLoop(geo, mat);
    loop.renderOrder = 3;
    return loop;
  }

  /** Rellena la geometría con un rectángulo redondeado (en el plano XZ) de radio r. */
  _setRoundedRect(geo, hw, hd, r, y) {
    const segs = geo.userData.segs;
    r = Math.min(r, hw, hd);
    const arr = geo.attributes.position.array;
    // 4 esquinas (centro, ángulo inicial) recorridas en sentido antihorario.
    const corners = [
      { cu: hw - r, cv: hd - r, a: 0 },              // +x,+z
      { cu: -(hw - r), cv: hd - r, a: Math.PI / 2 }, // -x,+z
      { cu: -(hw - r), cv: -(hd - r), a: Math.PI },  // -x,-z
      { cu: hw - r, cv: -(hd - r), a: 3 * Math.PI / 2 }, // +x,-z
    ];
    let i = 0;
    for (const c of corners) {
      for (let s = 0; s <= segs; s++) {
        const ang = c.a + (s / segs) * (Math.PI / 2);
        arr[i++] = c.cu + r * Math.cos(ang);
        arr[i++] = y;
        arr[i++] = c.cv + r * Math.sin(ang);
      }
    }
    geo.attributes.position.needsUpdate = true;
  }

  _applyValidation(validation) {
    if (!validation?.results) return;
    for (const r of validation.results) {
      const word = this.game.board.getWord(r.id);
      if (!word) continue;
      for (const t of word.tiles) {
        const mesh = this.meshes.get(t.instanceId);
        if (mesh) mesh.userData.valid = r.valid;
      }
    }
    this._updateRowLabels(this.game.board);
  }

  // ---------------------------------------------------------------------------
  // Interacción (Pointer Events: ratón + táctil + stylus)
  // ---------------------------------------------------------------------------
  _bind() {
    const c = this.sm.canvas;
    this.pointers = new Map(); // pointerId -> {x,y} (para pinch/pan multitáctil)
    this._pinch = null;
    this._onDown = (e) => this._pointerDown(e);
    this._onMove = (e) => this._pointerMove(e);
    this._onUp = (e) => this._pointerUp(e);
    this._onWheel = (e) => this._wheel(e);
    c.addEventListener('pointerdown', this._onDown);
    window.addEventListener('pointermove', this._onMove);
    window.addEventListener('pointerup', this._onUp);
    window.addEventListener('pointercancel', this._onUp);
    c.addEventListener('wheel', this._onWheel, { passive: false });
  }

  _pointerCentroidDist() {
    const pts = [...this.pointers.values()];
    if (pts.length < 2) return null;
    const [a, b] = pts;
    return {
      cx: (a.x + b.x) / 2,
      cy: (a.y + b.y) / 2,
      dist: Math.hypot(a.x - b.x, a.y - b.y),
    };
  }

  _setPointer(e) {
    this.pointer.x = (e.clientX / this.sm.vw()) * 2 - 1;
    this.pointer.y = -(e.clientY / this.sm.vh()) * 2 + 1;
  }

  _groundPoint() {
    this.raycaster.setFromCamera(this.pointer, this.sm.camera);
    const pt = new THREE.Vector3();
    this.raycaster.ray.intersectPlane(this.plane, pt);
    return pt;
  }

  _pickTile() {
    this.raycaster.setFromCamera(this.pointer, this.sm.camera);
    const hits = this.raycaster.intersectObjects([...this.meshes.values()], false);
    return hits.length ? hits[0].object : null;
  }

  _pointerDown(e) {
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Dos dedos: desplazamiento + pinch. Cancela cualquier arrastre.
    if (this.pointers.size === 2) {
      this._cancelDrag();
      this._candidate = null;
      this._panning = null;
      this._panCentroid = this._centroid();
      this._pinchDist = this._pointerCentroidDist()?.dist || null;
      return;
    }
    if (this.pointers.size > 2) return;

    this._setPointer(e);
    const mesh = this._interactive ? this._pickTile() : null;
    this._pointerDownPos = { x: e.clientX, y: e.clientY };
    if (mesh) {
      // Sobre una ficha -> candidato para arrastrarla.
      this._candidate = mesh;
      this._panning = null;
      this.bus.emit(EVENTS.PLAY_SFX, { name: 'pick' });
    } else {
      // En vacío -> desplazar el tablero (pan).
      this._candidate = null;
      this._panning = { x: e.clientX, y: e.clientY };
    }
  }

  _centroid() {
    const pts = [...this.pointers.values()];
    if (!pts.length) return { x: 0, y: 0 };
    const sx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const sy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    return { x: sx, y: sy };
  }

  _pointerMove(e) {
    if (this.pointers.has(e.pointerId)) this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Durante pan/arrastre/pinch, evita que el SO lo tome como gesto y saque de la app.
    if ((this.dragged || this._panning || this.pointers.size >= 2) && e.cancelable) e.preventDefault();

    // Dos dedos: desplazamiento + pinch (zoom, hasta el origen).
    if (this.pointers.size >= 2) {
      this._fitMode = false;
      const now = this._centroid();
      if (this._panCentroid) {
        this.sm.panByPixels(now.x - this._panCentroid.x, now.y - this._panCentroid.y);
      }
      this._panCentroid = now;
      const d = this._pointerCentroidDist()?.dist;
      if (d && this._pinchDist) this.sm.adjustZoom(d / this._pinchDist);
      this._pinchDist = d || this._pinchDist;
      return;
    }

    // Desplazamiento con un dedo/ratón sobre vacío.
    if (this._panning && !this.dragged) {
      this._fitMode = false;
      this.sm.panByPixels(e.clientX - this._panning.x, e.clientY - this._panning.y);
      this._panning = { x: e.clientX, y: e.clientY };
      return;
    }

    if (!this._interactive) return;
    this._setPointer(e);
    if (this._candidate && !this.dragged && this._pointerDownPos) {
      const dx = e.clientX - this._pointerDownPos.x;
      const dy = e.clientY - this._pointerDownPos.y;
      if (Math.hypot(dx, dy) > 6) this._startDrag(this._candidate, { dx, dy });
    }
    if (this.dragged) {
      const pt = this._groundPoint();
      if (this.dragged.mode === 'word') {
        const dx = pt.x - this.dragged.startPt.x;
        const dz = pt.z - this.dragged.startPt.z;
        this.dragged.meshes.forEach((m, i) => {
          const s = this.dragged.starts[i];
          m.position.set(s.x + dx, THICK / 2 + LIFT, s.z + dz);
        });
      } else {
        this.dragged.mesh.position.x = pt.x;
        this.dragged.mesh.position.z = pt.z;
        this.dragged.mesh.position.y = THICK / 2 + LIFT;
      }
    }
  }

  _cancelDrag() {
    if (this.dragged) {
      if (this.dragged.mode === 'word') this.dragged.meshes.forEach((m) => m.scale.setScalar(1));
      else this.dragged.mesh.scale.setScalar(1);
      this.dragged = null;
      this.sync(); // devuelve la(s) ficha(s) a su sitio
    }
  }

  /** Doble toque: alterna entre ver TODO (encaje) y acercar al máximo en un punto. */
  /** Doble toque: REORGANIZA las palabras (sin solapes) y ajusta el zoom para que
   *  TODAS entren en el tablero y en pantalla. */
  /** Reorganiza y encaja (público): se llama en momentos "asentados" (tras jugar la IA o
   *  confirmar el humano) para que las palabras aprovechen TODO el ancho de la pantalla. */
  reorganize() { this._handleDoubleTap(); }

  _handleDoubleTap() {
    this._fitMode = true;
    this._userArranged = true; // recordar la petición: en red se re-aplica tras cada sync
    if (this.game.board) {
      this._reorganizeWords(this.game.board);
      this._layoutBoard(this.game.board);
      if (this._separateWords(this.game.board)) this._layoutBoard(this.game.board);
      this._updateRowLabels(this.game.board); // recoloca los recuadros con las palabras
    }
    this._updateCameraFraming();
  }

  /**
   * Reorganiza TODAS las palabras en una cuadrícula ordenada. En vez de apilarlas en una
   * única columna central, las reparte en filas aprovechando el ANCHO del tablero (espacio a
   * izquierda y derecha), sin solapes, para que se vean todas de un vistazo.
   */
  _reorganizeWords(board) {
    const words = board.words.filter((w) => w.tiles && w.tiles.length);
    if (!words.length) return;
    const widthOf = (w) => w.tiles.reduce((s, t) => s + tileWidth(t) + GAP, -GAP);
    const gapX = 1.5;                 // aire horizontal entre palabras de una misma fila
    // Zona útil (proporción real de la pantalla, muy apaisada en móvil).
    let usableW = 1000, usableH = 400;
    try { const u = this.sm._usableArea(); if (u && u.usableH > 0) { usableW = u.usableW; usableH = u.usableH; } } catch { /* fallback */ }
    // Reparte las palabras en la CUADRÍCULA que hace las fichas lo más GRANDES posible,
    // aprovechando todo el ancho de la pantalla en varias filas. Probamos cada nº de filas
    // (1..N) y nos quedamos con el que maximiza el tamaño de ficha. En pantalla apaisada eso
    // da varias palabras por fila y varias filas (como un tablero); NUNCA una columna vertical
    // de 1 palabra por fila (que desperdicia el ancho y encoge las fichas), que era el fallo.
    const N = words.length;
    // Distribución equilibrada en R filas: los índices se reparten por columnas para que
    // todas las filas tengan casi el mismo nº de palabras (no una fila llena y otra a medias).
    const splitInto = (R) => {
      const perRow = Math.ceil(N / R);
      const cols = Math.ceil(N / perRow); // filas reales
      const cand = Array.from({ length: cols }, () => []);
      for (let i = 0; i < N; i++) cand[Math.floor(i / perRow)].push(words[i]);
      return cand.filter((r) => r.length);
    };
    let best = null; // { rows, s } con el MAYOR tamaño de ficha
    for (let R = 1; R <= N; R++) {
      const cand = splitInto(R);
      const bw = Math.max(...cand.map((r) => r.reduce((s, w) => s + widthOf(w) + gapX, -gapX)));
      const bh = cand.length * ROW_Z_STEP;
      const s = Math.min(usableW / (bw + 2.2), usableH / (bh + 2.2));
      if (!best || s > best.s + 1e-6) best = { rows: cand, s }; // '>' estricto: empate -> menos filas
    }
    let rows = best ? best.rows : [words];
    // Cada fila centrada en X; las filas se apilan en Z centradas en el ancla del tablero.
    let z = BOARD_ANCHOR_Z - ((rows.length - 1) / 2) * ROW_Z_STEP;
    for (const row of rows) {
      const rw = row.reduce((s, w) => s + widthOf(w) + gapX, -gapX);
      let x = -rw / 2;
      for (const w of row) {
        const ww = widthOf(w);
        w.x = x + ww / 2;
        w.z = z;
        x += ww + gapX;
      }
      z += ROW_Z_STEP;
    }
  }

  _startDrag(mesh, dir = null) {
    const tile = mesh.userData.tile;
    // Comodines de herramienta: un TOQUE los activa; un ARRASTRE los mueve/retira.
    // (Ya no se bloquea el arrastre, para poder quitarlos de la mesa.)

    // Arrastrar la PRIMERA ficha de una palabra de varias mueve la palabra ENTERA (Rummikub)...
    // ...SALVO que el gesto sea claramente VERTICAL (perpendicular a la fila): en ese caso el
    // jugador quiere SEPARAR/desbloquear esa primera sílaba, así que la sacamos suelta.
    if (!mesh.userData.inRack) {
      const word = this.game.board.findWordContaining(tile.instanceId);
      if (word && word.tiles.length > 1 && word.tiles[0].instanceId === tile.instanceId) {
        const detachFirst = dir && Math.abs(dir.dy) > Math.abs(dir.dx) * 1.2;
        if (!detachFirst) {
          const startPt = this._groundPoint();
          const meshes = word.tiles.map((t) => this.meshes.get(t.instanceId)).filter(Boolean);
          const starts = meshes.map((m) => m.position.clone());
          this.dragged = { mode: 'word', wordId: word.id, word, meshes, starts, startPt };
          return;
        }
      }
    }
    this.dragged = { mode: 'tile', mesh, tile, fromRack: mesh.userData.inRack };
  }

  _pointerUp(e) {
    this.pointers.delete(e.pointerId);
    if (this.pointers.size < 2) this._panCentroid = null;
    this._lastClientY = e.clientY;
    const wasPanning = this._panning;
    this._panning = null;

    // DOBLE TOQUE: acerca al máximo en ese punto / vuelve a ver todo.
    const movedLittle = this._pointerDownPos && Math.hypot(e.clientX - this._pointerDownPos.x, e.clientY - this._pointerDownPos.y) < 8;
    if (this.pointers.size === 0 && !this.dragged && movedLittle) {
      const now = performance.now();
      if (this._lastTap && (now - this._lastTap.t) < 320 && Math.hypot(e.clientX - this._lastTap.x, e.clientY - this._lastTap.y) < 34) {
        this._lastTap = null;
        this._handleDoubleTap(e.clientX, e.clientY);
        this._candidate = null; this._pointerDownPos = null; this.selected = null;
        return;
      }
      this._lastTap = { t: now, x: e.clientX, y: e.clientY };
    }

    // Tras soltar un dedo de un gesto de dos, o si estábamos desplazando, no hay toque/arrastre.
    if (this.pointers.size >= 1 || wasPanning) { this._candidate = null; this._pointerDownPos = null; return; }

    if (!this._interactive) return;
    if (this.dragged) {
      this._setPointer(e);
      const pt = this._groundPoint();
      if (this.dragged.mode === 'word') {
        const dx = pt.x - this.dragged.startPt.x;
        const dz = pt.z - this.dragged.startPt.z;
        const nz = Math.min((this.dragged.word.z ?? 0) + dz, 6); // límite razonable en la mesa
        this.commit('moveWord', this.dragged.wordId, (this.dragged.word.x ?? 0) + dx, nz);
        this._userArranged = false; // colocó una palabra a mano: respeta su orden (deja de auto-ordenar)
        this.dragged.meshes.forEach((m) => m.scale.setScalar(1));
      } else {
        this._resolveDrop(pt);
        this.dragged.mesh.scale.setScalar(1);
      }
      this.dragged = null;
    } else if (this._candidate) {
      this._handleTap(this._candidate);
    }
    this._candidate = null;
    this._pointerDownPos = null;
  }

  /** Toque simple: seleccionar / colocar / usar comodín (accesible y fiable en móvil). */
  _handleTap(mesh) {
    const tile = mesh.userData.tile;

    // Modo de selección de objetivo para un comodín de acción.
    if (this.pendingWild) {
      this._handleWildTarget(tile);
      return;
    }

    // Tocar un comodín activa su acción (lo resuelve App: universal o herramienta).
    if (tile.type === 'wildcard') {
      this.bus.emit('scene:wildcard', { tile });
      return;
    }

    if (this.selected && this.selected.instanceId === tile.instanceId) {
      this._clearSelection();
      return;
    }
    this._selectTile(tile, mesh);
  }

  // ---------------------------------------------------------------------------
  // Comodines de acción: modo de selección de objetivo
  // ---------------------------------------------------------------------------
  beginWildTargeting(wildTile) {
    this.pendingWild = { tile: wildTile, kind: wildTile.wildKind };
    this.mergeSel = [];
    this._clearSelection();
    // Resalta el comodín activo.
    const m = this.meshes.get(wildTile.instanceId);
    if (m) m.userData.selectedGlow = true;
  }

  endWildTargeting() {
    if (this.pendingWild) {
      const m = this.meshes.get(this.pendingWild.tile.instanceId);
      if (m) m.userData.selectedGlow = false;
    }
    // Limpia resaltados de la selección de fusión.
    for (const id of this.mergeSel || []) {
      const mm = this.meshes.get(id);
      if (mm) mm.userData.selectedGlow = false;
    }
    this.pendingWild = null;
    this.mergeSel = [];
  }

  _handleWildTarget(tile) {
    const kind = this.pendingWild.kind;
    const wildId = this.pendingWild.tile.instanceId;

    if (kind === 'split') {
      const res = this.commit('splitTile', tile.instanceId, wildId);
      this.bus.emit('scene:wildResult', { ...(res && typeof res === 'object' ? res : { ok: true }), kind });
      this.endWildTargeting();
      return;
    }

    if (kind === 'transform') {
      const options = this.game.transformOptions(tile.instanceId);
      if (!options.length) {
        this.bus.emit('scene:wildResult', { ok: false, reason: 'Esta ficha no se puede transformar.', kind });
        this.endWildTargeting();
        return;
      }
      // Delega la elección en App (modal); no cerramos el modo hasta resolver.
      this.bus.emit('scene:wildTransform', { target: tile, wildId, options });
      return;
    }

    if (kind === 'merge') {
      this.mergeSel = this.mergeSel || [];
      if (this.mergeSel.includes(tile.instanceId)) return;
      this.mergeSel.push(tile.instanceId);
      const m = this.meshes.get(tile.instanceId);
      if (m) m.userData.selectedGlow = true;
      // Con dos extremos, fusiona el rango completo entre ellos.
      if (this.mergeSel.length >= 2) {
        const word = this.game.board.findWordContaining(this.mergeSel[0]);
        if (word) {
          const idxs = this.mergeSel.map((id) => word.tiles.findIndex((t) => t.instanceId === id)).sort((a, b) => a - b);
          const rangeIds = [];
          for (let i = idxs[0]; i <= idxs[idxs.length - 1]; i++) rangeIds.push(word.tiles[i].instanceId);
          const res = this.commit('mergeTiles', rangeIds, wildId);
          this.bus.emit('scene:wildResult', { ...(res && typeof res === 'object' ? res : { ok: true }), kind });
        }
        this.endWildTargeting();
      }
    }
  }

  _selectTile(tile, mesh) {
    this._clearSelection();
    this.selected = tile;
    mesh.userData.selectedGlow = true;
    this.bus.emit('scene:tileSelected', { tile });
  }

  _clearSelection() {
    if (this.selected) {
      const m = this.meshes.get(this.selected.instanceId);
      if (m) m.userData.selectedGlow = false;
    }
    this.selected = null;
  }

  /** Coloca la ficha seleccionada en una palabra (usado por botones del HUD). */
  placeSelectedInWord(wordId) {
    if (!this.selected) return false;
    const tile = this.selected;
    const method = tile && this.game.activePlayer.rack.has(tile.instanceId) ? 'placeTileFromRack' : 'moveTileOnBoard';
    const ok = tile ? this.commit(method, tile.instanceId, wordId, -1) : false;
    this._clearSelection();
    return ok;
  }

  placeSelectedNewWord() {
    if (!this.selected) return false;
    const tile = this.selected;
    const method = this.game.activePlayer.rack.has(tile.instanceId) ? 'placeTileFromRack' : 'moveTileOnBoard';
    const ok = this.commit(method, tile.instanceId, null, -1);
    this._clearSelection();
    return ok;
  }

  /** Resuelve dónde soltar la ficha del TABLERO arrastrada. */
  _resolveDrop(pt) {
    const { tile } = this.dragged;
    const id = tile.instanceId;

    // Soltada SOBRE LA MANO (parte baja de la pantalla) -> devolver a la mano
    // (solo si se colocó este turno).
    const handTop = (typeof this.getHandTopY === 'function' ? this.getHandTopY() : null)
      ?? (this.sm.vh() * 0.8);
    if (this._lastClientY != null && this._lastClientY > handTop - 4) {
      if (this.game.state.turnStart.rackIds.includes(id)) {
        this.commit('returnTileToRack', id);
        this.bus.emit(EVENTS.PLAY_SFX, { name: 'drop' });
        return;
      }
      this.sync();
      return;
    }

    // ¿Se soltó CERCA de una palabra existente? -> unirse a ella (por el lado más próximo).
    const near = this._nearestWord(pt, id);
    if (near) {
      const index = this._indexInRow(near, pt.x, id);
      this.commit('moveTileOnBoard', id, near.wordId, index);
      this.bus.emit(EVENTS.PLAY_SFX, { name: 'snap' });
      return;
    }

    // Espacio libre -> NUEVA palabra EXACTAMENTE donde se soltó (colocación libre, Rummikub).
    this.commit('moveTileOnBoard', id, null, -1, { x: pt.x, z: pt.z });
    this.bus.emit(EVENTS.PLAY_SFX, { name: 'snap' });
  }

  /** Palabra existente más próxima al punto de soltado (para unirse a ella), o null. */
  _nearestWord(pt, draggedId) {
    let best = null;
    let bestD = Infinity;
    for (const row of this.targets.rows) {
      if (row.centers.length === 1 && row.centers[0].id === draggedId) continue; // no a sí misma
      if (Math.abs(pt.z - row.z) > DEPTH * 1.1) continue;
      const left = row.x - row.halfW - 1.3;
      const right = row.x + row.halfW + 1.3;
      if (pt.x < left || pt.x > right) continue;
      const d = Math.abs(pt.z - row.z) + Math.abs(pt.x - row.x) * 0.15;
      if (d < bestD) { bestD = d; best = row; }
    }
    return best;
  }

  _indexInRow(row, x, draggedId) {
    let index = 0;
    for (const c of row.centers) {
      if (c.id === draggedId) continue;
      if (x > c.x) index++;
    }
    return index;
  }

  _wheel(e) {
    e.preventDefault();
    // La rueda hace ZOOM (hasta el origen como máximo). Desactiva el encaje automático.
    this._fitMode = false;
    this.sm.adjustZoom(e.deltaY < 0 ? 1.12 : 0.9);
  }

  // ---------------------------------------------------------------------------
  // Efectos y animación
  // ---------------------------------------------------------------------------
  playEvolutionEffect(ev) {
    // Explosión de partículas doradas sobre la última fila.
    const count = 40;
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(count * 3);
    const vel = [];
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 2;
      pos[i * 3 + 1] = 0.5;
      pos[i * 3 + 2] = BOARD_ANCHOR_Z;
      vel.push(new THREE.Vector3((Math.random() - 0.5) * 3, Math.random() * 4 + 2, (Math.random() - 0.5) * 3));
    }
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const mat = new THREE.PointsMaterial({ color: 0xffd54a, size: 0.28, transparent: true });
    const points = new THREE.Points(geo, mat);
    this.group.add(points);
    this._particles.push({ points, vel, life: 1.2, geo, mat });
    this.bus.emit(EVENTS.PLAY_SFX, { name: 'evolution' });
  }

  /** ¿Este mesh se está arrastrando ahora mismo (ficha suelta o dentro de una palabra)? */
  _isDragging(mesh) {
    if (!this.dragged) return false;
    if (this.dragged.mode === 'word') return this.dragged.meshes.includes(mesh);
    return this.dragged.mesh === mesh;
  }

  _update(dt) {
    // Animaciones "pop" al validar palabras: escala de las fichas afectadas.
    const popScale = new Map();
    for (const pop of this._pops) {
      pop.t += dt * 3.2;
      const s = 1 + 0.42 * Math.sin(Math.min(1, pop.t) * Math.PI);
      for (const m of pop.meshes) popScale.set(m, Math.max(popScale.get(m) || 0, s));
    }
    this._pops = this._pops.filter((p) => p.t < 1);

    // Ondas expansivas (crecen y se desvanecen) al conseguir una palabra.
    for (const r of this._rings) {
      r.t += dt * 2.1;
      r.ring.scale.setScalar(1 + r.t * 6);
      r.mat.opacity = Math.max(0, 0.9 * (1 - r.t));
    }
    this._rings = this._rings.filter((r) => {
      if (r.t >= 1) { this.group.remove(r.ring); r.geo.dispose(); r.mat.dispose(); return false; }
      return true;
    });

    // Destello del contorno al validar (mezcla verde → blanco y se desvanece).
    for (const outline of this.rowLabels.values()) {
      const f = outline.userData.flash || 0;
      if (f > 0) {
        outline.userData.flash = Math.max(0, f - dt * 2.4);
        const c = new THREE.Color('#59e06a').lerp(new THREE.Color('#ffffff'), outline.userData.flash);
        outline.material.color.copy(c);
      }
    }

    // Lerp de fichas hacia su posición objetivo.
    for (const mesh of this.meshes.values()) {
      if (this._isDragging(mesh)) continue; // no interferir con fichas arrastradas
      const tgt = mesh.userData.target;
      mesh.position.x += (tgt.x - mesh.position.x) * Math.min(1, dt * 12);
      mesh.position.y += (tgt.y - mesh.position.y) * Math.min(1, dt * 12);
      mesh.position.z += (tgt.z - mesh.position.z) * Math.min(1, dt * 12);
      if (popScale.has(mesh)) {
        mesh.scale.setScalar(popScale.get(mesh));
      } else {
        const glow = mesh.userData.selectedGlow;
        const targetScale = glow ? 1.08 : 1;
        const s = mesh.scale.x + (targetScale - mesh.scale.x) * Math.min(1, dt * 10);
        mesh.scale.setScalar(s);
      }
    }
    // Los contornos (recuadros verdes) SIGUEN a sus fichas mientras se animan, para que al
    // reorganizar (doble toque) se recoloquen junto a las palabras y no se queden atrás.
    if (this.game.board) {
      for (const [id, outline] of this.rowLabels) {
        const word = this.game.board.getWord(id);
        if (!word || !word.tiles.length) continue;
        // Centro de la CAJA que envuelve las fichas (no la media de centros: con
        // fichas de distinto ancho no coinciden y el recuadro quedaría descuadrado).
        let minX = Infinity, maxX = -Infinity, sz = 0, n = 0;
        for (const t of word.tiles) {
          const m = this.meshes.get(t.instanceId);
          if (!m) continue;
          const hw = tileWidth(t) / 2;
          minX = Math.min(minX, m.position.x - hw);
          maxX = Math.max(maxX, m.position.x + hw);
          sz += m.position.z; n++;
        }
        if (n) outline.position.set((minX + maxX) / 2, 0, sz / n);
      }
    }
    // Partículas.
    for (const p of this._particles) {
      p.life -= dt;
      const arr = p.geo.attributes.position.array;
      for (let i = 0; i < p.vel.length; i++) {
        p.vel[i].y -= 9.8 * dt;
        arr[i * 3] += p.vel[i].x * dt;
        arr[i * 3 + 1] += p.vel[i].y * dt;
        arr[i * 3 + 2] += p.vel[i].z * dt;
      }
      p.geo.attributes.position.needsUpdate = true;
      p.mat.opacity = Math.max(0, p.life / 1.2);
    }
    this._particles = this._particles.filter((p) => {
      if (p.life <= 0) {
        this.group.remove(p.points);
        p.geo.dispose();
        p.mat.dispose();
        return false;
      }
      return true;
    });
  }

  _disposeMesh(mesh) {
    this.group.remove(mesh);
    if (Array.isArray(mesh.material)) mesh.material.forEach((m) => m.dispose());
    else mesh.material.dispose();
  }

  dispose() {
    this._removeUpdater?.();
    this._offBoard?.();
    this._offTurn?.();
    this._offEvo?.();
    this._offValidated?.();
    const c = this.sm.canvas;
    c.removeEventListener('pointerdown', this._onDown);
    window.removeEventListener('pointermove', this._onMove);
    window.removeEventListener('pointerup', this._onUp);
    window.removeEventListener('pointercancel', this._onUp);
    c.removeEventListener('wheel', this._onWheel);
    for (const mesh of this.meshes.values()) this._disposeMesh(mesh);
    this.meshes.clear();
    for (const outline of this.rowLabels.values()) {
      this.group.remove(outline);
      outline.geometry?.dispose();
      outline.material?.dispose();
    }
    this.rowLabels.clear();
    for (const geo of this._geomTop.values()) geo.dispose();
    this._geomTop.clear();
    this.sm.scene.remove(this.group);
  }
}

// --- utilidades locales ---
function shade(hex, amt) {
  const h = (hex || '#e8dcc0').replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const f = (v) => Math.max(0, Math.min(255, Math.round(v + 255 * amt)));
  return (f(r) << 16) | (f(g) << 8) | f(b);
}
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
